import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { PoolTable } from '../../types'
import type { GameSession } from '../../types'
import {fetchTableDetails, fetchTables, pauseTable, resumeTable, startTable} from "../thunks/tableThunks.ts";

interface TableData {
    table: PoolTable
    session: GameSession | null
    lastUpdated: number
}

interface TablesState {
    // All tables list
    tables: PoolTable[]
    tablesLoading: boolean
    tablesError: string | null

    // Individual table details (cached)
    tableDetailsCache: Record<number, TableData>

    // UI state
    selectedTableId: number | null

    // Metadata
    lastFetch: number | null
}

const initialState: TablesState = {
    tables: [],
    tablesLoading: false,
    tablesError: null,
    tableDetailsCache: {},
    selectedTableId: null,
    lastFetch: null
}

const tablesSlice = createSlice({
    name: 'tables',
    initialState,
    reducers: {
        setSelectedTable: (state, action: PayloadAction<number | null>) => {
            state.selectedTableId = action.payload
        },
        clearTableCache: (state, action: PayloadAction<number>) => {
            delete state.tableDetailsCache[action.payload]
        },
        clearAllCache: (state) => {
            state.tableDetailsCache = {}
        }
    },
    extraReducers: (builder) => {
        // Fetch all tables
        builder.addCase(fetchTables.pending, (state) => {
            state.tablesLoading = true
            state.tablesError = null
        })
        builder.addCase(fetchTables.fulfilled, (state, action) => {
            state.tables = action.payload
            state.tablesLoading = false
            state.lastFetch = Date.now()

            // Dashboard list is authoritative for status. Drop or refresh details-cache
            // entries so TableDetailModal cannot keep a stale IDLE/ACTIVE view after
            // another device starts/stops a table.
            const listById = new Map(action.payload.map(table => [table.id, table]))
            for (const tableId of Object.keys(state.tableDetailsCache).map(Number)) {
                const listTable = listById.get(tableId)
                const cached = state.tableDetailsCache[tableId]
                if (!listTable || listTable.status !== cached.table.status) {
                    delete state.tableDetailsCache[tableId]
                } else {
                    cached.table = listTable
                }
            }
        })
        builder.addCase(fetchTables.rejected, (state, action) => {
            state.tablesLoading = false
            state.tablesError = action.payload as string
        })

        // Fetch table details
        builder.addCase(fetchTableDetails.fulfilled, (state, action) => {
            const { tableId, table, session } = action.payload
            state.tableDetailsCache[tableId] = {
                table,
                session,
                lastUpdated: Date.now()
            }

            // Also update in tables array
            const index = state.tables.findIndex(t => t.id === tableId)
            if (index !== -1) {
                state.tables[index] = table
            }
        })

        // Pause/Resume success - tables will be refreshed by thunk
        builder.addCase(pauseTable.fulfilled, (state) => {
            // UI feedback could go here
        })
        builder.addCase(resumeTable.fulfilled, (state) => {
            // UI feedback could go here
        })
        builder.addCase(startTable.pending, (state, action) => {
            // UI feedback could go here
        })
        builder.addCase(startTable.fulfilled, (state, action) => {
            // UI feedback could go here
        })
        builder.addCase(startTable.rejected, (state, action) => {
            // UI feedback could go here
        })
    }
})

export const { setSelectedTable, clearTableCache, clearAllCache } = tablesSlice.actions
export default tablesSlice.reducer